import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';
import FeatureFlagWebClient, {
  AnalyticsClientInterface,
  ClientOptions,
  EnvironmentType,
  FeatureFlagUser,
  Identifiers,
} from '@atlassiansox/feature-flag-web-client';
import {
  RawFlags,
  SupportedFlagTypes,
} from '@atlassiansox/feature-flag-web-client/src/api/types';

import { FeatureFlagsType } from './allowedFeatureFlags';
import { applyPersistedOverrides } from './featureFlagPersistedOverrides';

let client: FeatureFlagWebClient | undefined;
export let processedFlags: FeatureFlagsType = {};

const processFlags = (rawFlags: RawFlags) => {
  return Object.entries(rawFlags).reduce((acc: FeatureFlagsType, [key, flag]) => {
    acc[key] = flag.value;
    return acc;
  }, {});
};

export const getClient = (): FeatureFlagWebClient | undefined => client;

export const getFlagValue = (
  key: string,
  defaultValue: SupportedFlagTypes
): SupportedFlagTypes => {
  const rawFlags = getClient()?.getFlags();

  if (!rawFlags) {
    return defaultValue;
  }

  processedFlags = processFlags(rawFlags);
  const flagsWithOverrides = applyPersistedOverrides(processedFlags ?? {});

  return flagsWithOverrides[key] ?? defaultValue;
};

// Used for client reset
export const setClient = (newClient: FeatureFlagWebClient | undefined): void => {
  client = newClient;
};

export const initializeFeatureFlagClient = async (
  analyticsClient: AnalyticsClientInterface,
  envType: 'stg-east' | 'prod-east',
  cloudId: undefined | string,
  accountId: string | undefined
): Promise<FeatureFlagWebClient> => {
  if (client) {
    return client;
  }

  const env = ((): EnvironmentType => {
    switch (envType) {
      case 'prod-east':
        return EnvironmentType.PROD;
      case 'stg-east':
      default:
        return EnvironmentType.STAGING;
    }
  })();

  const clientOptions: ClientOptions = {
    environment: env,
    productKey: 'confluence-databases',
    loggerOptions: {
      enabled: env === EnvironmentType.STAGING,
    },
  };

  client = new FeatureFlagWebClient(
    getApiKey(env),
    analyticsClient,
    getFeatureFlagUser(cloudId, accountId),
    clientOptions
  );

  await client.ready();
  const rawFlags = getClient()?.getFlags();

  if (rawFlags) {
    processedFlags = processFlags(rawFlags);
  }

  setBooleanFeatureFlagResolver((flagKey) => {
    return [
      'platform.linking-platform.datasource.show-jlol-basic-filters',
      'platform.linking-platform.datasource.show-clol-basic-filters',
    ].includes(flagKey);
  });

  return client;
};

// https://developer.atlassian.com/platform/frontend-feature-flags/resources/api-keys/
function getApiKey(env: EnvironmentType) {
  switch (env) {
    case EnvironmentType.PROD:
      return 'aeca1e40-8176-415e-869c-b1c469b55bab';
    case EnvironmentType.STAGING:
    case EnvironmentType.DEV:
    default:
      return 'df01e0d1-19ce-4891-86e4-725dc439fc30';
  }
}

function getFeatureFlagUser(cloudId?: string, accountId?: string): FeatureFlagUser {
  let user: FeatureFlagUser;

  if (cloudId) {
    user = {
      identifier: {
        type: Identifiers.TENANT_ID,
        value: cloudId,
      },
      isAnonymous: false,
      additionalIdentifiers: {},
    };
  } else {
    user = {
      identifier: {
        type: Identifiers.FF_CLIENT_ANONYMOUS_ID,
        value: 'anonymous',
      },
      isAnonymous: true,
      additionalIdentifiers: {},
    };
  }

  if (accountId) {
    user.additionalIdentifiers = {
      atlassianAccountId: accountId,
    };
  }

  return user;
}

export function getFeatureFlagsInUse(obj: Record<string, any>): string[] {
  const featureFlagsInUse: string[] = [];

  for (const key in obj) {
    if (obj[key]?.value === true) {
      featureFlagsInUse.push(key);
    }
  }

  return featureFlagsInUse;
}
